import { useCallback } from 'react';
import { isBefore } from 'date-fns';
import { USER_QRCODE_DATA } from '@lib/enums/localStorageKeys';
import { addSecondsFn } from '@lib/utils/dateTimeHelpers';

interface CodeEntry {
  code: string;
  createdAt: string; // ISO date format
}

export const DEFAULT_EXPIRATION_SECONDS = 30 * 60; // 30 minutes in seconds

// Exported for unit testing: the env var is already expressed in seconds
// (see VITE_SCANNED_CODE_EXPIRATION_SECONDS in .env* files), it must not be
// multiplied by 60.
export function parseExpirationSeconds(rawValue?: string): number {
  return rawValue ? parseInt(rawValue, 10) : DEFAULT_EXPIRATION_SECONDS;
}

const EXPIRATION_SECONDS = parseExpirationSeconds(
  import.meta.env.VITE_SCANNED_CODE_EXPIRATION_SECONDS,
);

// Exported for unit testing the expiry boundary against EXPIRATION_SECONDS.
export function isCodeValid(codeEntry: CodeEntry): boolean {
  if (!EXPIRATION_SECONDS) return false;
  const expiresAt = addSecondsFn(
    new Date(codeEntry.createdAt),
    EXPIRATION_SECONDS,
  );
  return !!expiresAt && isBefore(new Date(), expiresAt);
}

function useManageCodes() {
  const getCodesFromStorage = (): CodeEntry[] => {
    const storedCodes = localStorage.getItem(USER_QRCODE_DATA);
    if (!storedCodes) return [];
    return JSON.parse(storedCodes).filter(isCodeValid);
  };

  // Set or update a code
  const setCode = useCallback((newCode: string) => {
    const now = new Date().toISOString();
    const updatedCodes = getCodesFromStorage();
    const existingIndex = updatedCodes.findIndex((c) => c.code === newCode);

    if (existingIndex > -1) {
      updatedCodes[existingIndex].createdAt = now; // Update existing code's timestamp
    } else {
      updatedCodes.push({ code: newCode, createdAt: now }); // Add new code
    }

    localStorage.setItem(USER_QRCODE_DATA, JSON.stringify(updatedCodes));
  }, []);

  // Check if a code exists and is valid
  const checkCode = useCallback(
    (codeToCheck: string) =>
      getCodesFromStorage().some((c) => {
        if (!c.code?.length) return false;
        return c.code.indexOf(codeToCheck) > -1;
      }),
    [],
  );

  // Public API of the hook
  return { setCode, checkCode };
}

export default useManageCodes;
